import React, { useEffect, useMemo, useState } from "react";
import {
  fechaDeActividad,
  ordenarActividades,
} from "../../../utils/actividades";
import {
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Alert,
  Progress,
  Row,
  Table,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
  ReactDataTable,
} from "../../../components/Component";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { useDataFetch, deleteData } from "../../../hooks/useDataFetch";
import useAjustes from "../../../hooks/useAjustes";
import CampaignInfoCard from "../../../components/partials/campaign/campaign-info-card/CampaignInfoCard";
import { useNavigate } from "react-router";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import ResultadosImportacion from "./resultados-importacion";
import { formatBytes } from "../../../utils/file";

// Servidor de las fotos. En pruebas las imágenes viven en apitest, así que
// fijarlo en el código hacía que el panel de pruebas mostrara las de producción.
// El valor por defecto es producción para no alterar el despliegue actual.
/** Color del distintivo de cada actividad, para poder distinguirlas de un vistazo. */
const COLOR_ACTIVIDAD = {
  Siembra: "bg-success",
  Seguimiento: "bg-primary",
  Resiembra: "bg-warning text-dark",
};

/**
 * Los datos propios de cada tipo de actividad, como pares etiqueta/valor.
 *
 * Se devuelven en una lista en vez de escribir el marcado de cada caso: antes
 * eran dos ternarios anidados con un <ul> dentro de <CardText>, que renderiza
 * un <p> —una lista dentro de un párrafo es marcado inválido y el navegador la
 * sacaba fuera—.
 */
const datosDeActividad = (foto) => {
  if (foto.activity_type === "Seguimiento") {
    return [
      ["Altura", foto.size_height != null ? `${foto.size_height} cm` : null],
      ["Diámetro", foto.size_dimension != null ? `${foto.size_dimension} cm` : null],
    ].filter(([, valor]) => valor);
  }

  if (foto.activity_type === "Resiembra") {
    return [
      ["Nombre común", foto.common_name],
      ["Familia", foto.family],
      ["Especie", foto.species],
      ["Distribución", foto.origin_distribution],
    ].filter(([, valor]) => valor);
  }

  return [];
};

/**
 * Foto de una actividad, con sus tres estados.
 *
 * Igual que en el mapa público: mientras la imagen viaja se ve un esqueleto
 * en movimiento, y si no llega se queda un aviso en su sitio. Las fotos del
 * histórico pesan más de un megabyte, así que sin esto la tarjeta se queda
 * un buen rato con un hueco gris que no explica nada.
 *
 * `placeholder-wave` va en el padre y `placeholder` en lo que parpadea: es
 * como lo monta Bootstrap.
 */
const FotoActividad = ({ src, alt }) => {
  const [estado, setEstado] = useState("cargando");

  return (
    <div className="bg-light rounded-top overflow-hidden">
      {estado === "cargando" && (
        <div className="placeholder-wave">
          <span className="placeholder d-block w-100 h-100" />
        </div>
      )}

      {estado === "error" && (
        <div className="d-flex align-items-center justify-content-center text-muted small">
          Imagen no disponible
        </div>
      )}

      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="w-100 h-100"
        style={{
          objectFit: "cover",
          opacity: estado === "lista" ? 1 : 0,
          transition: "opacity .3s ease",
        }}
        onLoad={() => setEstado("lista")}
        onError={() => setEstado("error")}
      />
    </div>
  );
};

const PHOTOS_URL =
  process.env.REACT_APP_PHOTOS_URL || "https://ecosistema-allianz.com";

// El Excel viaja dentro de la petición de importación, así que tiene un tope
// real (el servidor corta en 256 MB). El ZIP se sube por partes y no lo tiene,
// pero un archivo de varios GB significa horas de subida: es mejor avisar antes
// de empezar que dejar al usuario mirando una barra de progreso.
const MAX_EXCEL_SIZE = 32 * 1024 * 1024;
const MAX_ZIP_SIZE = 2 * 1024 * 1024 * 1024;

const validarArchivos = ({ excel, zip }) => {
  const archivoExcel = excel?.[0];
  const archivoZip = zip?.[0];

  if (archivoExcel && archivoExcel.size > MAX_EXCEL_SIZE) {
    return `El Excel pesa ${formatBytes(archivoExcel.size)} y el máximo es ${formatBytes(MAX_EXCEL_SIZE)}.`;
  }

  if (archivoZip && archivoZip.size > MAX_ZIP_SIZE) {
    return (
      `El ZIP pesa ${formatBytes(archivoZip.size)} y el máximo es ${formatBytes(MAX_ZIP_SIZE)}. ` +
      "Divídelo en varias partes y súbelas una por una con el mismo Excel."
    );
  }

  return null;
};

// El texto pesa tanto como la barra: con un ZIP grande el proceso tarda varios
// minutos, y sin saber en qué etapa va (subiendo, optimizando fotos,
// actualizando árboles) la pantalla parece colgada.
const AvanceImportacion = ({ avance }) => (
  <Row>
    <Col md={12}>
      <div className="mb-3">
        <small className="text-soft d-block mb-1">
          {avance?.texto ?? "Preparando el envío..."}
        </small>
        <Progress
          value={avance?.porcentaje ?? 0}
          animated={(avance?.porcentaje ?? 0) < 100}
        />
      </div>
    </Col>
  </Row>
);

// El tamaño es la señal más útil antes de subir: un ZIP de cientos de MB tarda
// varios minutos y conviene que se vea desde que se elige el archivo.
const TamanoArchivo = ({ archivo }) =>
  archivo ? (
    <small className="text-soft d-block mt-1">
      {archivo.name} — {formatBytes(archivo.size)}
    </small>
  ) : null;

const ListTreePage = () => {
  const navigate = useNavigate();
  // Un formulario por modal. Antes compartían una sola instancia y, como los dos
  // registran campos llamados "excel" y "zip", se pisaban entre sí: tras subir
  // una vez, el segundo modal quedaba con el estado del primero.
  const formNew = useForm();
  const formUpdate = useForm();

  const [count, setCount] = useState({ tree: 0, location: 0 });
  const [responseCreate, setResponseCreate] = useState([]);
  const [responseUpdate, setResponseUpdate] = useState([]);
  const [items, setItems] = useState([]);
  const [modalNew, setModalNew] = useState(false);
  const [modalUpdate, setModalUpdate] = useState(false);
  const [infoTree, setInfoTree] = useState({
    labels: [],
    datasets: [
      {
        label: "Fechas de siembra",
        borderWidth: 2,
        // borderColor: "transparent",
        // hoverBorderColor: "transparent",
        borderSkipped: "bottom",
        barPercentage: 0.8,
        categoryPercentage: 0.8,
        backgroundColor: [],
        data: [],
      },
    ],
  });

  const [infoLocation, setInfoLocation] = useState({
    labels: [],
    datasets: [
      {
        label: "Lugares de siembra",
        borderWidth: 2,
        // borderColor: "transparent",
        // hoverBorderColor: "transparent",
        borderSkipped: "bottom",
        barPercentage: 0.8,
        categoryPercentage: 0.8,
        backgroundColor: [],
        data: [],
      },
    ],
  });

  // Avance de cada importación: { porcentaje, texto }, o null si no hay
  // ninguna en curso. El texto cambia según la etapa (subiendo, optimizando
  // fotos, actualizando árboles), que es lo único que dice al usuario si el
  // proceso sigue vivo durante los minutos que tarda un ZIP grande.
  // Mensajes de error que devuelve el API (formato inválido, ZIP faltante...).
  const [errorCreate, setErrorCreate] = useState(null);
  const [errorUpdate, setErrorUpdate] = useState(null);
  const [progressCreate, setProgressCreate] = useState(null);
  const [progressUpdate, setProgressUpdate] = useState(null);

  const { getData, invalidateQuery, deleteData, postImport } = useDataFetch();

  const { data: info, refetch: refetchInfo } = getData(
    ["treesinfo"],
    "/panel/trees/info",
    {
      enabled: true,
    }
  );

  // Tipos válidos para la columna «tipo» del Excel de nuevos árboles. Solo se
  // listan en los modales con el ajuste activo (ver useAjustes).
  const { tiposDeArbol } = useAjustes();
  const { data: treeTypes } = getData(["tree-types"], "/panel/tree-types", {
    enabled: tiposDeArbol,
  });
  const nombresDeTipos = Array.isArray(treeTypes)
    ? treeTypes.map((tipo) => tipo.name)
    : [];

  const { data, isLoading, refetch } = getData(["trees"], "/panel/trees", {
    enabled: true,
    onSuccess: (data) => {
      // La tabla necesita un arreglo; si llega otra cosa se deja vacía.
      setItems(Array.isArray(data) ? data : []);
    },
  });

  const { mutate: deleteTree } = deleteData(
    "/panel/trees",
    {},
    {
      onSuccess: (data) => {
        refreshTable();
      },
    }
  );

  const { mutate: deletePhoto } = deleteData(
    "/panel/trees/photo",
    {},
    {
      onSuccess: (data) => {
        refreshTable();
      },
    }
  );

  const { mutate: uploadNew, isLoading: isLoadingSave } = postImport({
    onProgreso: ({ porcentaje, texto }) =>
      setProgressCreate({ porcentaje, texto }),
    onError: (error) => {
      setProgressCreate(null);
      setErrorCreate(error?.message || "No fue posible completar la importación.");
    },
    onSuccess: (data) => {
      setProgressCreate(null);
      if (data?.trees) {
        setResponseCreate(data.trees);
        formNew.reset();
        refetchInfo();
        refreshTable();
        toast.success("Se completo la acción correctamente.");
      } else {
        toast.error("No fue posible subir los árboles.");
      }
    },
  });

  const { mutate: uploadUpdate, isLoading: isLoadingUpdate } = postImport({
    onProgreso: ({ porcentaje, texto }) =>
      setProgressUpdate({ porcentaje, texto }),
    onError: (error) => {
      setProgressUpdate(null);
      setErrorUpdate(error?.message || "No fue posible completar la importación.");
    },
    onSuccess: (data) => {
      setProgressUpdate(null);
      if (data?.trees) {
        setResponseUpdate(data.trees);
        formUpdate.reset();
        refetchInfo();
        refreshTable();
        toast.success("Se completo la acción correctamente.");
      } else {
        toast.error("No fue posible subir los árboles.");
      }
    },
  });

  useEffect(() => {
    if (!info) return;

    // La respuesta puede no traer los listados: sesión vencida, error del API o
    // simplemente sin datos. Antes se asumía que existían y la página entera se
    // caía con "Cannot read properties of undefined".
    const porUbicacion = Array.isArray(info.treesByLocation)
      ? info.treesByLocation
      : [];
    const porFecha = Array.isArray(info.treesByPlantingDate)
      ? info.treesByPlantingDate
      : [];

    setCount({
      tree: info.totalTrees ?? 0,
      location: info.totalUniqueLocations ?? 0,
    });

    setInfoLocation((prev) => ({
      ...prev,
      labels: porUbicacion.map((e) => e.location),
      datasets: [
        { ...prev.datasets[0], data: porUbicacion.map((e) => e.count) },
      ],
      backgroundColor: "rgba(255,255,255)",
    }));

    setInfoTree((prev) => ({
      ...prev,
      labels: porFecha.map((e) => e.planting_date),
      datasets: [{ ...prev.datasets[0], data: porFecha.map((e) => e.count) }],
      backgroundColor: "rgba(255,255,255)",
    }));
  }, [info]);

  const dataTableColumns2 = useMemo(
    () => [
      {
        name: "Código",
        selector: (row) => row.code,
        cell: (row) => <span>{row.code}</span>,
        sortable: true,
      },
      {
        name: "Nombre Común",
        selector: (row) => row.common_name,
        cell: (row) => <span>{row.common_name}</span>,
        sortable: true,
      },

      {
        name: "Familia",
        selector: (row) => row.family,
        cell: (row) => <span>{row.family}</span>,
        sortable: true,
      },
      {
        name: "Especie",
        selector: (row) => row.species,
        cell: (row) => <span>{row.species}</span>,
        sortable: true,
      },

      {
        name: "Distribución",
        selector: (row) => row.origin_distribution,
        cell: (row) => <span>{row.origin_distribution}</span>,
        sortable: true,
      },
      {
        name: "Tipo",
        selector: (row) => row.type,
        cell: (row) => <span>{row.type}</span>,
        sortable: true,
      },

      {
        name: "Fecha de Plantación",
        selector: (row) => row.planting_date,
        cell: (row) => <span>{row.planting_date}</span>,
        sortable: true,
      },
      {
        name: "Acciones",
        cell: (row) => (
          <Button onClick={() => handleDelete(row.id)}>
            <Icon name="trash" size={20} />
          </Button>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    [data]
  );

  const refreshTable = async () => {
    invalidateQuery(["trees"]);
    await refetch();
  };

  const handleDelete = (id) => {
    deleteTree({ id });
  };

  const toggleNew = () => setModalNew(!modalNew);
  const toggleUpdate = () => setModalUpdate(!modalUpdate);

  const onFormSubmitNew = (data) => {
    setErrorCreate(null);

    const error = validarArchivos(data);
    if (error) {
      setErrorCreate(error);
      return;
    }

    // Sin la comprobación del ZIP, no adjuntarlo lanzaba un TypeError y el
    // botón Guardar parecía no responder.
    uploadNew({
      tipo: "create",
      excel: data.excel[0],
      zip: data.zip?.[0] ?? null,
    });
  };

  const onFormSubmitUpdate = (data) => {
    setErrorUpdate(null);

    const error = validarArchivos(data);
    if (error) {
      setErrorUpdate(error);
      return;
    }

    // Sin la comprobación del ZIP, no adjuntarlo lanzaba un TypeError y el
    // botón Guardar parecía no responder.
    uploadUpdate({
      tipo: "update",
      excel: data.excel[0],
      zip: data.zip?.[0] ?? null,
    });
  };

  const expandableRowComponent = ({ data }) => {
    return (
      <div className="row p-5">
        <div>
          <h6>Información</h6>
          <table className="table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Voluntario</th>
                <th>Área Allianzer</th>
                <th>Departamento</th>
                <th>Ciudad</th>
                <th>Ubicación</th>
                <th>Longitud</th>
                <th>Latitud</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{data.type}</td>
                <td>{data.volunteer_name}</td>
                <td>{data.allianzer_area ?? "N/A"}</td>
                <td>{data.department}</td>
                <td>{data.city}</td>
                <td>{data.location}</td>
                <td>{data.longitude}</td>
                <td>{data.latitude}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="col-12 mt-4">
          {/*
            La sección incluye Siembra, Seguimiento y Resiembra, así que el
            título ya no puede llamarse «Seguimiento».
          */}
          <h6 className="mb-3">Actividades</h6>

          {/*
            `g-4` da la separación entre tarjetas: la fila no llevaba gutter y
            las de la segunda línea quedaban pegadas a las de arriba. Y
            `row-cols-*` las hace responsivas, que con `col-3` fijo se
            aplastaban en pantallas estrechas.
          */}
          <Row className="g-4 row-cols-1 row-cols-md-2 row-cols-lg-3">
            {ordenarActividades(data.photos, data).map((e) => (
              <Col key={e.id}>
                {/*
                  `h-100` iguala la altura de todas las tarjetas de la fila.
                  Sin esto, la de Siembra —que no lleva datos— quedaba a media
                  altura y la cuadrícula salía escalonada.
                */}
                <Card className="h-100 border-0 shadow-sm">
                  {/*
                    Las fotos vienen de cámaras distintas y con proporciones
                    distintas. Encajarlas en 4:3 recortando deja la cuadrícula
                    pareja.
                  */}
                  <FotoActividad
                    src={`${PHOTOS_URL}${e.photo_url}`}
                    alt={`${e.activity_type} del árbol ${data.code}`}
                  />

                  <CardBody className="d-flex flex-column">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span
                        className={`badge ${COLOR_ACTIVIDAD[e.activity_type] ?? "bg-secondary"}`}
                      >
                        {e.activity_type}
                      </span>
                      <small className="text-muted">
                        {String(fechaDeActividad(e, data) ?? "").slice(0, 10)}
                      </small>
                    </div>

                    {datosDeActividad(e).length > 0 && (
                      <dl className="row g-0 mb-2 small">
                        {datosDeActividad(e).map(([etiqueta, valor]) => (
                          <React.Fragment key={etiqueta}>
                            <dt className="col-5 text-muted fw-normal">
                              {etiqueta}
                            </dt>
                            <dd className="col-7 mb-1">{valor}</dd>
                          </React.Fragment>
                        ))}
                      </dl>
                    )}

                    {e.description && (
                      <p className="small text-muted mb-0">{e.description}</p>
                    )}

                    {/*
                      `mt-auto` empuja el botón al pie de la tarjeta, así todas
                      lo tienen a la misma altura aunque el texto varíe.
                    */}
                    <div className="mt-auto pt-3">
                      <Button
                        color="danger"
                        outline
                        size="sm"
                        title="Eliminar esta actividad"
                        onClick={() => deletePhoto({ id: e.id })}
                      >
                        <Icon name="trash" />
                        <span className="ms-1">Eliminar</span>
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <Head title="Árboles"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Árboles</BlockTitle>
              <BlockDes className="text-soft">
                <p>Espacio para administrar la información de los árboles.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <Button
                  className={`btn-icon btn-trigger toggle-expand me-n1`}
                  onClick={() => {}}
                >
                  <Icon name="more-v" />
                </Button>
                <div className="toggle-expand-content">
                  <ul className="nk-block-tools g-3">
                    <li>
                      <Button
                        className=" btn-primary"
                        onClick={() =>
                          navigate(`${process.env.PUBLIC_URL}/tree`)
                        }
                      >
                        <Icon name="plus"></Icon>
                        <span>Nuevo Árbol</span>
                      </Button>
                    </li>
                    <li className="nk-block-tools-opt">
                      <Button
                        className="btn-dim btn-white btn-outline-light"
                        onClick={() => {
                          formNew.reset();
                          setErrorCreate(null);
                          setResponseCreate([]);
                          toggleNew();
                        }}
                      >
                        <Icon name="upload"></Icon>
                        <span>Cargar Nuevos Árboles</span>
                      </Button>
                    </li>
                    <li className="nk-block-tools-opt">
                      <Button
                        className="btn-dim btn-white btn-outline-light"
                        onClick={() => {
                          formUpdate.reset();
                          setErrorUpdate(null);
                          setResponseUpdate([]);
                          toggleUpdate();
                        }}
                      >
                        <Icon name="upload"></Icon>
                        <span>Seguimiento de Árboles</span>
                      </Button>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Block>
          <Row className="g-gs">
            <Col lg="6" sm="6">
              <CampaignInfoCard
                theme="primary"
                title="Cantidad de árboles sembrados"
                amount={count.tree}
                change=""
                direction="up"
                chart="bar"
                chartData={infoTree}
              />
            </Col>
            <Col lg="6" sm="6">
              <CampaignInfoCard
                theme="info"
                title="Cantidad de lugares donde se han sembrado"
                amount={count.location}
                change=""
                direction="up"
                chart="bar"
                chartData={infoLocation}
              />
            </Col>
          </Row>
        </Block>

        <Card className="mt-4">
          <div className="">
            <div className={` p-4`}>
              <ReactDataTable
                pending={isLoading}
                data={items}
                columns={dataTableColumns2}
                pagination
                className="nk-tb-list"
                expandableRows={true}
                ExpandableRowComponent={expandableRowComponent}
              />
            </div>
          </div>
        </Card>
      </Content>

      <Modal isOpen={modalNew} toggle={toggleNew} size="lg">
        <Form onSubmit={formNew.handleSubmit(onFormSubmitNew)}>
          <ModalHeader toggle={toggleNew}>Cargar Nuevos Árboles</ModalHeader>
          <ModalBody>
            <Alert color="primary">
              Para árboles que se registran <strong>por primera vez</strong>. El
              archivo debe tener <strong>16 columnas (A–P)</strong> y terminar en{" "}
              <code>fecha_siembra, nombre_foto</code>. Si el archivo es de
              seguimiento o resiembra, usa <strong>Seguimiento de Árboles</strong>.
            </Alert>
            <h5>Instrucciones</h5>
            <ul>
              <li>
                Las columnas del archivo EXCEL deben estar en el siguiente
                orden:
                <strong>
                  codigo, tipo, familia, especie, nombre_común, origen
                </strong>{" "}
                (Endémica, Nativa, Casi endémica),
                <strong>tipo</strong>,
                <strong>
                  nombre_voluntario, allianzer, departamento, ciudad, lugar,
                  longitud, latitud, fecha_siembra, nombre_foto
                </strong>
                .
              </li>
              {tiposDeArbol ? (
                <li>
                  La columna <strong>tipo</strong> (la séptima, G) debe tener
                  uno de estos valores:{" "}
                  {nombresDeTipos.length > 0 ? (
                    nombresDeTipos.map((nombre, i) => (
                      <React.Fragment key={nombre}>
                        {i > 0 && ", "}
                        <code>{nombre}</code>
                      </React.Fragment>
                    ))
                  ) : (
                    <em>cargando los tipos...</em>
                  )}
                  . No importan las mayúsculas. Si la celda está vacía se
                  asigna <strong>Voluntario</strong>. Un tipo que no esté en la
                  lista deja la fila sin guardar; los tipos nuevos se crean en{" "}
                  <strong>Maestros → Tipos de árbol</strong>.
                </li>
              ) : (
                <li>
                  La columna <strong>tipo</strong> (la séptima, G) indica el
                  tipo del participante. Si la celda está vacía se asigna{" "}
                  <strong>Voluntario</strong>.
                </li>
              )}
              <li>
                El campo <strong>fecha_siembra</strong> puede ser una celda con
                formato de fecha de Excel o un texto <code>AÑO-MES-DÍA</code>.
              </li>
              <li>
                El campo <strong>código</strong> no debe repetirse. En caso de
                subir un código existente, el sistema actualizará la información
                correspondiente y avisará si cambia la familia, la especie o el
                nombre común.
              </li>
              <li>
                Las fotos deben subirse en un archivo ZIP, con el mismo nombre
                que aparece en la columna <strong>nombre_foto</strong> del EXCEL.
                Pueden ir en la raíz o dentro de carpetas, y no importan las
                mayúsculas de la extensión.
              </li>
            </ul>

            <Row className="mt-3">
              <Col md={6}>
                <FormGroup>
                  <Label for="excel-nuevo">Archivo de EXCEL</Label>

                  <Controller
                    name="excel"
                    control={formNew.control}
                    rules={{
                      required: true,
                    }}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <>
                        <Input
                          id="excel-nuevo"
                          type="file"
                          accept=".xlsx"
                          onChange={(e) => onChange(e.target.files)}
                        />
                        <TamanoArchivo archivo={value?.[0]} />
                      </>
                    )}
                  />
                  {formNew.formState.errors.excel && (
                    <span className="invalid">Este campo es requerido</span>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="zip-nuevo">Arhivo ZIP</Label>
                  <Controller
                    name="zip"
                    control={formNew.control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <>
                        <Input
                          id="zip-nuevo"
                          type="file"
                          accept=".zip"
                          onChange={(e) => onChange(e.target.files)}
                        />
                        <TamanoArchivo archivo={value?.[0]} />
                      </>
                    )}
                  />

                  {formNew.formState.errors.zip && (
                    <span className="invalid">Este campo es requerido</span>
                  )}
                </FormGroup>
              </Col>
            </Row>

            {/*
              Visible durante toda la espera, no solo mientras llegan eventos
              de progreso: al ocultarse el botón Guardar, esta es la única señal
              de que la operación sigue en marcha.
            */}
            {isLoadingSave && <AvanceImportacion avance={progressCreate} />}

            <ResultadosImportacion items={responseCreate} />
          </ModalBody>
          <ModalFooter>
            {(formNew.formState.errors.excel ||
              errorCreate) && (
              <Alert color="danger" className="w-100 mb-2">
                {formNew.formState.errors.excel
                  ? "Selecciona el archivo EXCEL antes de guardar."
                  : errorCreate}
              </Alert>
            )}
            <Button
              // Sin type="button" el navegador lo trata como submit y Cancelar
              // también enviaba el formulario.
              type="button"
              color="secondary"
              onClick={toggleNew}
            >
              {/*
                Mientras se sube ya no cancela nada: la petición sigue su curso
                aunque se cierre el modal. Llamarlo «Cerrar» dice la verdad.
              */}
              {isLoadingSave ? "Cerrar" : "Cancelar"}
            </Button>
            {/*
              Se oculta mientras sube, para que no se pueda reenviar, y también
              cuando ya hay resultados en pantalla: la carga terminó y volver a
              pulsar solo repetiría la importación. El botón vuelve al abrir el
              modal de nuevo, que es cuando se limpian los resultados.
            */}
            {!isLoadingSave && responseCreate.length === 0 && (
              <Button color="primary">Guardar</Button>
            )}{" "}
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={modalUpdate} toggle={toggleUpdate} size="lg">
        <Form onSubmit={formUpdate.handleSubmit(onFormSubmitUpdate)}>
          <ModalHeader toggle={toggleUpdate}>
            Seguimiento de Árboles
          </ModalHeader>
          <ModalBody>
            <Alert color="info">
              Para árboles <strong>que ya existen</strong>. El archivo debe tener{" "}
              <strong>11 columnas (A–K)</strong> y terminar en{" "}
              <code>fecha, nombre_foto</code>, o <strong>12 (A–L)</strong> si
              incluye <code>tipo_arbol</code>. Si son árboles nuevos, usa{" "}
              <strong>Cargar Nuevos Árboles</strong>.
            </Alert>
            <h5>Instrucciones</h5>
            <ul>
              <li>
                Las columnas del archivo EXCEL deben estar en el siguiente
                orden:
                <strong>codigo, tipo </strong> (Seguimiento, Resiembra),{" "}
                <strong> familia, especie, nombre_común, origen</strong>{" "}
                (Endémica, Nativa, Casi endémica),
                <strong>altura, dimesiones, fecha, nombre_foto</strong> y,
                opcional, <strong>tipo_arbol</strong>.
              </li>
              <li>
                La columna <strong>tipo_arbol</strong> (la duodécima, L) es
                opcional y cambia el tipo del árbol, por ejemplo en una
                resiembra.
                {tiposDeArbol && (
                  <>
                    {" "}
                    Valores válidos:{" "}
                    {nombresDeTipos.length > 0 ? (
                      nombresDeTipos.map((nombre, i) => (
                        <React.Fragment key={nombre}>
                          {i > 0 && ", "}
                          <code>{nombre}</code>
                        </React.Fragment>
                      ))
                    ) : (
                      <em>cargando los tipos...</em>
                    )}
                    . No importan las mayúsculas. Un tipo que no esté en la
                    lista deja la fila sin guardar.
                  </>
                )}{" "}
                Si la columna no está o la celda está vacía, el árbol conserva
                su tipo. No confundir con la columna <strong>tipo</strong> (B),
                que es la actividad.
              </li>
              <li>
                El campo <strong>fecha</strong> puede ser una celda con formato
                de fecha de Excel o un texto <code>AÑO-MES-DÍA</code>.
              </li>
              <li>
                El campo <strong>código</strong> debe existir.
              </li>
              <li>
                Para <strong>corregir</strong> un seguimiento ya cargado, sube de
                nuevo la fila con la <strong>misma fecha</strong>: se reemplazan
                sus datos y no se crea otro registro. Una fecha nueva agrega un
                seguimiento nuevo.
              </li>
              <li>
                Las fotos deben subirse en un archivo ZIP, con el mismo nombre
                que aparece en la columna <strong>nombre_foto</strong> del EXCEL.
                Pueden ir en la raíz o dentro de carpetas, y no importan las
                mayúsculas de la extensión.
              </li>
            </ul>

            <Row className="mt-3">
              <Col md={6}>
                <FormGroup>
                  <Label for="excel-seguimiento">Archivo de EXCEL</Label>

                  <Controller
                    name="excel"
                    control={formUpdate.control}
                    rules={{
                      required: true,
                    }}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <>
                        <Input
                          id="excel-seguimiento"
                          type="file"
                          accept=".xlsx"
                          onChange={(e) => onChange(e.target.files)}
                        />
                        <TamanoArchivo archivo={value?.[0]} />
                      </>
                    )}
                  />
                  {formUpdate.formState.errors.excel && (
                    <span className="invalid">Este campo es requerido</span>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="zip-seguimiento">Arhivo ZIP</Label>
                  <Controller
                    name="zip"
                    control={formUpdate.control}
                    rules={{
                      required: true,
                    }}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <>
                        <Input
                          id="zip-seguimiento"
                          type="file"
                          accept=".zip"
                          onChange={(e) => onChange(e.target.files)}
                        />
                        <TamanoArchivo archivo={value?.[0]} />
                      </>
                    )}
                  />

                  {formUpdate.formState.errors.zip && (
                    <span className="invalid">Este campo es requerido</span>
                  )}
                </FormGroup>
              </Col>
            </Row>

            {/*
              Visible durante toda la espera, no solo mientras llegan eventos
              de progreso: al ocultarse el botón Guardar, esta es la única señal
              de que la operación sigue en marcha.
            */}
            {isLoadingUpdate && <AvanceImportacion avance={progressUpdate} />}

            <ResultadosImportacion items={responseUpdate} />
          </ModalBody>
          <ModalFooter>
            {(formUpdate.formState.errors.excel ||
              formUpdate.formState.errors.zip ||
              errorUpdate) && (
              <Alert color="danger" className="w-100 mb-2">
                {formUpdate.formState.errors.excel
                  ? "Selecciona el archivo EXCEL antes de guardar."
                  : formUpdate.formState.errors.zip
                  ? "Selecciona el archivo ZIP con las fotos antes de guardar."
                  : errorUpdate}
              </Alert>
            )}
            <Button
              // Sin type="button" el navegador lo trata como submit y Cancelar
              // también enviaba el formulario.
              type="button"
              color="secondary"
              onClick={toggleUpdate}
            >
              {/*
                Mientras se sube ya no cancela nada: la petición sigue su curso
                aunque se cierre el modal. Llamarlo «Cerrar» dice la verdad.
              */}
              {isLoadingUpdate ? "Cerrar" : "Cancelar"}
            </Button>
            {/*
              Se oculta mientras sube, para que no se pueda reenviar, y también
              cuando ya hay resultados en pantalla: la carga terminó y volver a
              pulsar solo repetiría la importación. El botón vuelve al abrir el
              modal de nuevo, que es cuando se limpian los resultados.
            */}
            {!isLoadingUpdate && responseUpdate.length === 0 && (
              <Button color="primary">Guardar</Button>
            )}{" "}
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default ListTreePage;
